import { ChartV2, type ChartV2Handle, DEFAULT_HORIZONS } from '@chartv2';
import { Button } from '@toggle/design-system';
import { SkeletonLoader } from '@toggle/design-system/src/components/skeleton-loader/SkeletonLoader';
import { capitalizeFirstLetter } from '@toggle/helpers';
import React, {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useSnakeMeta } from '~/react-query-hooks/snakes/useSnake';
import { Tracking } from '~/services/tracking';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { ChartModal } from '~/views/alfred/research/research-visualization/ChartModal';
import { useVisualizationStore } from '~/views/alfred/research/use-visualization-store/useVisualizationStore';
import {
  terminalPanesToDefaultHiddenSeriesIds,
  terminalPanesToDefaultPriceDisplayByPane,
  terminalPanesToSeries,
  toChartV2Domain,
  toChartV2Horizon,
  toChartV2Interval,
  toRangeHorizon,
  toResampleInterval,
} from '~/views/chart-v2/utils/map-terminal-chart';
import { useTurboChart } from '~/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import {
  checkSearchParams,
  getChartSettingsFromSearchParams,
  type ValidChartSettings,
} from '~/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import styles from './ChartV2MarkdownLink.module.scss';
import { downloadChartV2Xlsx } from './chart-v2-xlsx/downloadChartV2Xlsx';

export const CHARTV2_MARKDOWN_LINK_TITLE = 'line-chart';

// The 1D horizon relies on intraday resampling, which depends on the
// (now unsupported) prices API, so we drop it from the horizon selector.
const AVAILABLE_HORIZONS = DEFAULT_HORIZONS.filter(horizon => horizon !== '1D');

export const isChartV2MarkdownTitle = (title: string | undefined): boolean =>
  title?.toLowerCase().includes(CHARTV2_MARKDOWN_LINK_TITLE) ?? false;

export const isChartV2MarkdownHref = (href: string | undefined): boolean => {
  if (!href) {
    return false;
  }

  try {
    const url = new URL(href, window.location.origin);

    return url.pathname === '/app/chartv2';
  } catch {
    return false;
  }
};

export const getChartV2MarkdownSettings = (
  href: string | undefined
): ValidChartSettings | null => {
  if (!href) {
    return null;
  }

  try {
    const url = new URL(href, window.location.origin);

    if (!isChartV2MarkdownHref(href)) {
      return null;
    }

    const settings = getChartSettingsFromSearchParams(url.searchParams);

    return checkSearchParams(settings) ? settings : null;
  } catch {
    return null;
  }
};

export interface ChartV2MarkdownLinkProps {
  href?: string;
  label?: ReactNode;
}

export const ChartV2MarkdownLink = ({
  href,
  label,
}: ChartV2MarkdownLinkProps) => {
  const { t } = useTranslation(['common', 'widget']);
  const { trackAlfredChartAction } = usePosthogEvents();

  const chartV2Ref = useRef<ChartV2Handle>(null);
  const modalChartV2Ref = useRef<ChartV2Handle>(null);
  const chartNodeRef = useRef<HTMLDivElement | null>(null);
  const modalChartNodeRef = useRef<HTMLDivElement | null>(null);
  const appliedDomainKeyRef = useRef<string | null>(null);
  const inlineDomainRef = useRef<{ start: number; end: number } | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const settings = useMemo(() => getChartV2MarkdownSettings(href), [href]);
  const setCharts = useVisualizationStore(state => state.setCharts);
  const registerChart = useCallback(
    (node: HTMLDivElement | null) => {
      chartNodeRef.current = node;

      if (node && href) {
        setCharts(href, node);
      }
    },
    [href, setCharts]
  );

  const {
    chartState,
    enabledResamples,
    chartIsLoading,
    changeRange,
    changeResample,
  } = useTurboChart({
    initialChartSettings: settings ?? undefined,
    shouldUseSearchParams: false,
    disablePriceHistory: true,
  });

  const { horizon, resample, chartPanes } = chartState;
  const primaryAsset = chartPanes[0]?.chartAssetData[0];

  // The y-axis label is the snake's human-readable name (snake/v3). Prefer
  // the meta already on the asset; only fetch when it isn't there yet.
  const existingSnakeName = useMemo(
    () => primaryAsset?.snakeMeta?.name.english.trim(),
    [primaryAsset]
  );
  const snakeToFetch = useMemo(
    () =>
      existingSnakeName
        ? ''
        : (primaryAsset?.snakeMeta?.snake ??
          primaryAsset?.entity.default_snake ??
          ''),
    [existingSnakeName, primaryAsset]
  );
  const { data: primarySnakeMeta } = useSnakeMeta(snakeToFetch);

  const series = useMemo(() => terminalPanesToSeries(chartPanes), [chartPanes]);

  const domain = useMemo(
    () => toChartV2Domain(chartState.domain, primaryAsset),
    [chartState.domain, primaryAsset]
  );

  useEffect(() => {
    if (!domain || series.length === 0) {
      appliedDomainKeyRef.current = null;
      return;
    }

    const key = `${domain.start}:${domain.end}`;

    if (appliedDomainKeyRef.current === key) {
      return;
    }

    chartV2Ref.current?.setDomain(domain);
    appliedDomainKeyRef.current = key;
  }, [domain, series.length]);

  // Open the fullscreen chart on the inline chart's *current* visible range.
  // The inline chart reports user pan/zoom via onDomainChange (captured in
  // inlineDomainRef); fall back to the turbo domain before any interaction.
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const visibleDomain = inlineDomainRef.current ?? domain;

    if (visibleDomain) {
      modalChartV2Ref.current?.setDomain(visibleDomain);
    }
  }, [isModalOpen, domain]);

  const defaultHiddenSeriesIds = useMemo(
    () => terminalPanesToDefaultHiddenSeriesIds(chartPanes),
    [chartPanes]
  );

  // Single source of truth for the chart title so the inline header, the
  // expanded modal, and downloads all share the same casing.
  const displayLabel = useMemo(
    () =>
      typeof label === 'string' ? capitalizeFirstLetter(label.trim()) : label,
    [label]
  );

  const exportTitle = useMemo(() => {
    const labelTitle = typeof displayLabel === 'string' ? displayLabel : '';

    return labelTitle || series[0]?.label || 'chart';
  }, [displayLabel, series]);

  // Axis labels: time on the x-axis, and the snake's `name.english` (snake/v3)
  // on the y-axis, falling back to the series unit and then a generic "Price".
  const xAxisLabel = t('common:date');
  const yAxisLabel = useMemo(
    () =>
      existingSnakeName ||
      primarySnakeMeta?.name.english.trim() ||
      series[0]?.yAxisLabel?.trim() ||
      t('common:price'),
    [existingSnakeName, primarySnakeMeta, series, t]
  );

  // Export the series the user actually sees (the inline legend is hidden, so
  // default-hidden series are never shown). Fall back to all series if every
  // series happens to be hidden.
  const exportSeries = useMemo(() => {
    const hidden = new Set(defaultHiddenSeriesIds);
    const visible = series.filter(item => !hidden.has(item.id));

    return visible.length > 0 ? visible : series;
  }, [series, defaultHiddenSeriesIds]);

  const handleDownload = useCallback(
    async (chartNode: HTMLElement | null) => {
      try {
        const didDownload = await downloadChartV2Xlsx({
          series: exportSeries,
          title: exportTitle,
          xAxisLabel,
          yAxisLabel,
          chartNode,
        });

        if (didDownload) {
          trackAlfredChartAction(POSTHOG_TRACKING_SOURCES.ALFRED, 'download');
        }
      } catch (error) {
        Tracking.captureException(error);
      }
    },
    [exportSeries, exportTitle, xAxisLabel, yAxisLabel, trackAlfredChartAction]
  );

  const handleExpand = useCallback(() => {
    trackAlfredChartAction(POSTHOG_TRACKING_SOURCES.ALFRED, 'expand');
    setIsModalOpen(true);
  }, [trackAlfredChartAction]);

  const renderChart = (
    ref: Ref<ChartV2Handle>,
    onDomainChange?: (domain: { start: number; end: number }) => void
  ) => (
    <ChartV2
      ref={ref}
      className={styles.chart}
      series={series}
      horizon={toChartV2Horizon(horizon)}
      interval={toChartV2Interval(resample)}
      availableHorizons={AVAILABLE_HORIZONS}
      enabledIntervals={enabledResamples.map(toChartV2Interval)}
      showIntervalSelector={false}
      defaultHiddenSeriesIds={defaultHiddenSeriesIds}
      defaultPriceDisplayByPane={terminalPanesToDefaultPriceDisplayByPane(
        chartPanes
      )}
      onHorizonChange={next => changeRange(toRangeHorizon(next))}
      onIntervalChange={next => changeResample(toResampleInterval(next))}
      onDomainChange={onDomainChange}
      showDefaultLegend={false}
    />
  );

  // Wrap the chart with a rotated y-axis label on the left and an x-axis
  // label below. The chart node (passed in) stays the capture target — the
  // labels are HTML here and re-drawn onto the exported PNG separately.
  const renderAxisFrame = (chartElement: ReactNode) => (
    <div className={styles.chartArea}>
      <div className={styles.chartColumn}>
        {chartElement}
        <span className={styles.xAxisLabel}>{xAxisLabel}</span>
      </div>
      {yAxisLabel && (
        <span className={styles.yAxisLabel} title={yAxisLabel}>
          {yAxisLabel}
        </span>
      )}
    </div>
  );

  if (!settings) {
    return null;
  }

  if (chartIsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.root}>
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '100%',
                  height: '100%',
                },
              },
            ]}
            className={styles.skeletonLoader}
          />
        </div>
      </div>
    );
  }

  const renderDownloadButton = (getChartNode: () => HTMLElement | null) => (
    <Button
      iconName="CloudArrowDown"
      label={t('widget:download')}
      variant="empty"
      size="small"
      onClick={() => void handleDownload(getChartNode())}
    />
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          {displayLabel && (
            <span
              className={styles.title}
              title={
                typeof displayLabel === 'string' ? displayLabel : undefined
              }
            >
              {displayLabel}
            </span>
          )}
          <div className={styles.buttonGroup}>
            {renderDownloadButton(() => chartNodeRef.current)}
            <Button
              className={styles.desktopOnlyButton}
              iconName="Expand"
              label={t('common:expand')}
              variant="empty"
              size="small"
              onClick={handleExpand}
            />
          </div>
        </div>
        {renderAxisFrame(
          <div
            className={styles.root}
            data-testid="chartv2-markdown-link"
            ref={registerChart}
          >
            {renderChart(chartV2Ref, range => {
              inlineDomainRef.current = range;
            })}
          </div>
        )}
      </div>
      <ChartModal
        isOpen={isModalOpen}
        title={displayLabel}
        downloadButton={renderDownloadButton(() => modalChartNodeRef.current)}
        chart={renderAxisFrame(
          <div ref={modalChartNodeRef} className={styles.modalChart}>
            {renderChart(modalChartV2Ref)}
          </div>
        )}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
